/**
 * VENOA shared behaviour: scroll reveal for elements carrying `.venoa-reveal`.
 * Respects prefers-reduced-motion and is skipped inside the theme editor.
 */
(() => {
  const root = document.documentElement;
  root.classList.add('venoa-js');

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* Der Beobachtungsbereich reicht nach oben weit über das Fenster hinaus:
     Alles, was schon oberhalb des Bildschirms liegt, gilt als gesehen. Beim
     schnellen Wischen, bei Sprunglinks oder beim Zurück-Navigieren kann ein
     Element am sichtbaren Ausschnitt vorbeirauschen, ohne dass der Browser es
     dort je meldet (in Tests: bis zu 10 Texte auf der Startseite blieben
     unsichtbar). So wird es spätestens gemeldet, sobald es darüber liegt. */
  const ABOVE = '100000px';

  const observer =
    !reduceMotion && 'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              entry.target.classList.add('is-visible');
              observer.unobserve(entry.target);
            });
          },
          { rootMargin: `${ABOVE} 0px -8% 0px`, threshold: 0.1 }
        )
      : null;

  /**
   * Szenen (venoa-scenes.css): Bereiche mit [data-venoa-scene] bekommen
   * einmalig .is-visible, sobald ihre Oberkante das untere Fünftel des
   * Fensters erreicht. Ihre Kinder spielen dann einen zeitbasierten Auftritt
   * ab – in jedem Browser gleich (auch Safari ohne Scroll-Timelines), egal wie
   * schnell gescrollt wird. Weil der Auftritt immer ganz abläuft, bleibt beim
   * Anhalten nichts halb maskiert oder angeschnitten stehen.
   */
  const sceneObservers = [];
  const startScene = (el) => {
    el.classList.add('is-visible');
    sceneObservers.forEach((obs) => obs.unobserve(el));
  };

  if (!reduceMotion && 'IntersectionObserver' in window) {
    // Oberkante hat das untere Fünftel erreicht …
    sceneObservers.push(
      new IntersectionObserver(
        (entries) => entries.forEach((entry) => entry.isIntersecting && startScene(entry.target)),
        { rootMargin: `${ABOVE} 0px -18% 0px`, threshold: 0 }
      )
    );
    // … oder der Bereich ist zur Hälfte zu sehen (flache Elemente am Seitenende
    // erreichen die Linie oben sonst nie, z. B. das VENOA im Footer mobil).
    sceneObservers.push(
      new IntersectionObserver(
        (entries) => entries.forEach((entry) => entry.intersectionRatio >= 0.5 && startScene(entry.target)),
        { threshold: [0.5, 1] }
      )
    );
  }

  /**
   * Wiederholbare Auftritte: Verlässt ein Element den sichtbaren Bereich nach
   * unten (man scrollt zurück), wird sein Auftritt zurückgesetzt – beim
   * nächsten Herunterscrollen erscheint es wieder weich, beliebig oft.
   * - Weiche Einstiege (.venoa-reveal, Übergänge) laufen im untersten
   *   Randstreifen (3 %) zurück. Szenen und die zeitbasierten Archive-Einstiege
   *   erst, wenn sie ganz verschwunden sind – ihr Auftritt würde sonst
   *   sichtbar abbrechen.
   * - Eingeblendet wird erst höher (unteres Zwölftel, siehe oben): zwei
   *   getrennte Schwellen, kein Hin- und Herflackern. Mitten im Bild wird nie
   *   etwas ausgeblendet; was oben hinausgeht, bleibt stehen.
   * - Nie zurückgesetzt: Kaufbereich, Kopf- und Fußnavigation, der Inhalt der
   *   Duftstory (gewählter Duft bleibt), Gruppen mit Kaufbuttons oder
   *   Formularfeldern, offene Akkordeons und Bereiche mit Tastaturfokus.
   */
  const KEEP_ZONES = 'header, footer .group-block-content, .product-information, venoa-scent-story, dialog, #cart-drawer';
  const KEEP_INSIDE =
    'a.button, .button, button[type="submit"], input, select, textarea, details[open], [aria-expanded="true"]';
  const KEYFRAMED = '[data-venoa-scene], .venoa-archive-hero__media, .venoa-archive-story__media, .venoa-archive-compare__item';
  const keepShown = (el) => Boolean(el.closest(KEEP_ZONES) || el.matches(':focus-within') || el.querySelector(KEEP_INSIDE));
  const leftBelow = (entry) =>
    !entry.isIntersecting && entry.boundingClientRect.top >= (entry.rootBounds?.bottom ?? window.innerHeight) - 1;

  const resetEntrance = (el) => {
    if (!el.classList.contains('is-visible') || keepShown(el)) return;
    el.classList.remove('is-visible');
    if (el.matches('[data-venoa-scene]')) sceneObservers.forEach((obs) => obs.observe(el));
    else observer?.observe(el);
  };

  const resetObservers =
    observer && !root.classList.contains('shopify-design-mode')
      ? {
          edge: new IntersectionObserver(
            (entries) => entries.forEach((entry) => leftBelow(entry) && resetEntrance(entry.target)),
            { rootMargin: '0px 0px -3% 0px', threshold: 0 }
          ),
          gone: new IntersectionObserver(
            (entries) => entries.forEach((entry) => leftBelow(entry) && resetEntrance(entry.target)),
            { threshold: 0 }
          ),
        }
      : null;

  const watchResets = (scope) => {
    if (!resetObservers) return;
    scope.querySelectorAll('.venoa-reveal, [data-venoa-scene]').forEach((el) => {
      if (el.closest(KEEP_ZONES)) return;
      (el.matches(KEYFRAMED) ? resetObservers.gone : resetObservers.edge).observe(el);
    });
  };

  // Footer: Dwells Spaltenzeile wird auf allen Seiten zur Szene – die Spalten
  // steigen beim Erreichen des Seitenendes versetzt auf (Markup kommt aus der
  // Footer-Gruppe, daher hier markiert statt im Liquid).
  const FOOTER_SCENE = "footer .group-block > .group-block-content.layout-panel-flex--row";

  const init = (scope = document) => {
    scope.querySelectorAll(FOOTER_SCENE).forEach((row) => row.setAttribute('data-venoa-scene', ''));

    scope.querySelectorAll('[data-venoa-scene]:not(.is-visible)').forEach((el) => {
      if (sceneObservers.length && !root.classList.contains('shopify-design-mode')) {
        sceneObservers.forEach((obs) => obs.observe(el));
      } else {
        el.classList.add('is-visible');
      }
    });

    /* Staffelung: Kinder eines [data-venoa-stagger]-Containers bekommen
       nacheinander 80 ms Verzögerung, höchstens 400 ms – Inhalte sollen
       nicht auf sich warten lassen. Eigene --reveal-delay-Werte bleiben. */
    scope.querySelectorAll('[data-venoa-stagger]').forEach((group) => {
      group.querySelectorAll(':scope > .venoa-reveal').forEach((el, index) => {
        if (el.style.getPropertyValue('--reveal-delay')) return;
        el.style.setProperty('--reveal-delay', `${Math.min(index * 80, 400)}ms`);
      });
    });

    scope.querySelectorAll('.venoa-reveal:not(.is-visible)').forEach((el) => {
      if (observer && !root.classList.contains('shopify-design-mode')) {
        observer.observe(el);
      } else {
        el.classList.add('is-visible');
      }
    });

    watchResets(scope);
  };

  /**
   * Images: make casual saving harder — no context menu, no dragging out of
   * the page. This is deliberately NOT a copy protection: the files stay
   * reachable through dev tools, the network tab or a screenshot, and we do
   * not pretend otherwise. Only the everyday browser gestures are taken away.
   *
   * Both listeners are delegated and only act when the event actually starts
   * on an image, so right-click on text, links and buttons keeps working and
   * text stays selectable. Galleries, sliders, swipes and product links are
   * untouched — none of them rely on contextmenu or dragstart.
   */
  const isMedia = (target) =>
    target instanceof Element && target.closest('img, picture, video, .venoa-hero__card, .media');

  document.addEventListener('contextmenu', (event) => {
    if (isMedia(event.target)) event.preventDefault();
  });

  document.addEventListener('dragstart', (event) => {
    if (isMedia(event.target)) event.preventDefault();
  });

  /**
   * Warenkorb-Panel auf breiten Bildschirmen: Klick auf die abgedunkelte
   * Seite (.venoa-cart-scrim, siehe venoa-motion.css) schließt das Panel.
   * Escape übernimmt Dwells <theme-drawer>, die Fokusführung der Block unten.
   */
  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element) || !event.target.matches('[data-venoa-cart-scrim]')) return;
    const drawer = document.getElementById('cart-drawer');
    if (drawer && typeof drawer.close === 'function') drawer.close();
  });

  /**
   * Seite hinter dem offenen Warenkorb-Panel sperren.
   *
   * Auf breiten Bildschirmen ist Dwells Panel ein nicht-modaler Dialog; seine
   * Fokusfalle merkt sich die Elemente beim Öffnen und verliert sie, sobald
   * der Inhalt nach „In den Warenkorb" neu gerendert wird. Statt Tab-Tasten
   * abzufangen, wird die Seite selbst für die Dauer des Panels `inert`: Weder
   * Tastatur noch Maus noch Screenreader erreichen dann verdeckte Inhalte.
   * Dwell meldet Öffnen/Schließen über `theme-drawer:open|close` – das
   * Schließen-Ereignis kommt vor der Fokus-Rückgabe, der Auslöser ist dann
   * wieder erreichbar.
   */
  const isCartDrawer = (event) => event.target instanceof Element && event.target.id === 'cart-drawer';
  // Bleiben bedienbar: das Panel selbst, die Abdunkelung und das Cookie-Banner.
  const KEEP_INTERACTIVE = 'script, style, link, .venoa-cart-scrim, pandectes-cmp, venoa-consent';

  const setBackgroundInert = (drawer, on) => {
    for (const child of document.body.children) {
      if (on) {
        if (child.contains(drawer) || child.matches(KEEP_INTERACTIVE) || child.inert) continue;
        child.inert = true;
        child.dataset.venoaInert = '';
      } else if (child.hasAttribute('data-venoa-inert')) {
        child.inert = false;
        delete child.dataset.venoaInert;
      }
    }
  };

  document.addEventListener('theme-drawer:open', (event) => {
    if (isCartDrawer(event)) setBackgroundInert(event.target, true);
  });

  document.addEventListener('theme-drawer:close', (event) => {
    if (isCartDrawer(event)) setBackgroundInert(event.target, false);
  });

  /**
   * Vorsichtsmaßnahme für WebKit (Safari 26/27): In Tests hat WebKit nach
   * programmatischem Scrollen (Sprunglinks, Fokus per Tab, Zurück-Navigation)
   * scroll-gebundene Animationen nicht immer neu gezeichnet – Inhalte standen
   * dann bis zum nächsten echten Scrollen im Anfangszustand. Eine unsichtbare
   * Stil-Auffrischung am Ende jeder Scrollbewegung behebt das (Test: 3 von 11
   * Sprüngen ohne, 0 von 11 mit Auffrischung – gemessen in WebKit bei
   * verdecktem Fenster; ob es bei sichtbarem Safari auftritt, ist nicht belegt).
   * Kostet eine Stilberechnung pro Scroll-Ende und läuft nur in Browsern mit
   * Scroll-Timelines.
   */
  if (window.CSS && CSS.supports('animation-timeline: view()')) {
    let syncTimer = 0;
    let syncCount = 0;
    const syncScrollAnimations = () => {
      syncCount += 1;
      root.style.setProperty('--venoa-sda-sync', String(syncCount));
    };

    // scroll bubbelt nicht – in der Capture-Phase kommt aber jedes Scrollen an,
    // auch das des .page-wrapper (Desktop) und des Dokuments (Smartphone).
    document.addEventListener(
      'scroll',
      () => {
        window.clearTimeout(syncTimer);
        syncTimer = window.setTimeout(syncScrollAnimations, 90);
      },
      { capture: true, passive: true }
    );
    window.addEventListener('pageshow', syncScrollAnimations);
  }

  /**
   * Sicherheitsnetz für Auftritte: Kurz nach jedem Scroll-Stopp wird alles
   * eingeblendet, was im oder über dem Bild liegt, aber nie gemeldet wurde
   * (schnelles Wischen; auf dem Desktop scrollt .page-wrapper, dort hilft der
   * erweiterte Beobachtungsbereich oben nicht). Einmal pro Scroll-Stopp, nur
   * für noch unsichtbare Elemente, passiv. Der Listener bleibt aktiv, weil
   * Auftritte beim Zurückscrollen zurückgesetzt werden (siehe oben).
   */
  if (observer && !root.classList.contains('shopify-design-mode')) {
    const PENDING = '.venoa-reveal:not(.is-visible), [data-venoa-scene]:not(.is-visible)';
    let settleTimer = 0;

    const settle = () => {
      const limit = window.innerHeight * 0.92;
      let waiting = 0;
      document.querySelectorAll(PENDING).forEach((el) => {
        const rect = el.getBoundingClientRect();
        if (!rect.width && !rect.height) return; // ausgeblendet (z. B. mobile Variante auf dem Desktop)
        if (rect.top < limit) {
          el.classList.add('is-visible');
          observer.unobserve(el);
          sceneObservers.forEach((obs) => obs.unobserve(el));
        } else {
          waiting += 1;
        }
      });
      // Bleibt aktiv (auch ohne Wartende): Zurückgesetzte Auftritte kommen
      // beim nächsten Herunterscrollen wieder dazu.
      return waiting;
    };

    const onScrollSettle = () => {
      window.clearTimeout(settleTimer);
      settleTimer = window.setTimeout(settle, 150);
    };

    document.addEventListener('scroll', onScrollSettle, { capture: true, passive: true });
  }

  /**
   * Druck-Rückmeldung auf dem iPhone: Safari zeigt :active-Zustände nur, wenn
   * irgendwo ein touchstart-Listener hängt. Dieser tut nichts und ist passiv –
   * Scrollen wird nie aufgehalten.
   */
  document.addEventListener('touchstart', () => {}, { passive: true });

  /**
   * Kaufart gewechselt (Ein Duft / Bundle): markiert den Kaufbereich, damit
   * das neue Feld weich einblendet (venoa-theme.css). Nur Darstellung – die
   * Auswahl selbst steuert weiterhin venoa-buy-mode.js.
   */
  document.addEventListener('change', (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.closest('.venoa-buymode__switch')) return;
    input.closest('venoa-buy-mode, .venoa-buymode')?.setAttribute('data-venoa-switched', '');
  });

  init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
